import { authRequest } from './client';
import {
  type InvoiceAssetOptionItem,
  type InvoiceDetailResponse,
  type ListInvoicesParams,
  type ListInvoicesResponse,
} from './types';

// ─── backoffice-api invoices client ────────────────────────────────────────
//
// Thin fetch wrappers around backoffice-api's `/v1/invoices` endpoints
// (Task 9 — read-only across all merchants; Task 26 widened `listInvoices`'s
// filters/sort to match the backend's full `ListInvoicesQueryDto` and added
// `getInvoiceAssetOptions`). Both require INVOICES_VIEWER (or SUPERADMIN).

export function listInvoices(params: ListInvoicesParams = {}): Promise<ListInvoicesResponse> {
  const query = new URLSearchParams();

  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.status !== undefined) query.set('status', params.status);
  if (params.merchantId !== undefined) query.set('merchantId', params.merchantId);
  if (params.subStatus !== undefined) query.set('subStatus', params.subStatus);
  if (params.assetId !== undefined) query.set('assetId', params.assetId);
  if (params.fiatCurrency !== undefined) query.set('fiatCurrency', params.fiatCurrency);
  if (params.amountUsdMin !== undefined) query.set('amountUsdMin', params.amountUsdMin);
  if (params.amountUsdMax !== undefined) query.set('amountUsdMax', params.amountUsdMax);
  if (params.from !== undefined) query.set('from', params.from);
  if (params.to !== undefined) query.set('to', params.to);
  if (params.sortBy !== undefined) query.set('sortBy', params.sortBy);
  if (params.sortOrder !== undefined) query.set('sortOrder', params.sortOrder);

  const qs = query.toString();
  return authRequest<ListInvoicesResponse>(`/invoices${qs ? `?${qs}` : ''}`);
}

/** Uses the invoice's DB `id` (the same `id` field on the list item) — NOT `publicId`. */
export function getInvoice(invoiceId: string): Promise<InvoiceDetailResponse> {
  return authRequest<InvoiceDetailResponse>(`/invoices/${invoiceId}`);
}

/** Every asset (including disabled ones) for the invoices list's asset filter dropdown. */
export function getInvoiceAssetOptions(): Promise<InvoiceAssetOptionItem[]> {
  return authRequest<InvoiceAssetOptionItem[]>('/invoices/asset-options');
}

/**
 * Manual re-initiation for an ACTION_REQUIRED/SETTLEMENT_FAILED invoice —
 * INVOICES_MANAGER-only. Resets the sweep/gas-fund decline counters and
 * flips the invoice back to SETTLING so payment-async's sweep cron retries
 * it on its next tick.
 */
export function retryInvoiceSettlement(invoiceId: string): Promise<InvoiceDetailResponse> {
  return authRequest<InvoiceDetailResponse>(`/invoices/${invoiceId}/retry-settlement`, { method: 'POST' });
}
